package fr.frux.feature.map.aim

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Remove
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode
import fr.frux.core.maps.Location
import fr.frux.core.maps.MAX_MAP_ZOOM
import fr.frux.core.maps.MIN_MAP_ZOOM
import fr.frux.core.maps.MapCameraPosition
import fr.frux.core.maps.offsetOfLocation
import fr.frux.core.maps.rememberLocationPermissionController
import fr.frux.feature.map.LocateMeButton
import fr.frux.feature.map.LocationNotice
import fr.frux.feature.map.LocationNoticeBanner
import fr.frux.feature.map.MapHostState
import fr.frux.feature.map.onLocateRequested
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_aim_pin_description
import fr.frux.feature.map.resources.map_aim_pin_outside_description
import fr.frux.feature.map.resources.map_aim_zoom_in
import fr.frux.feature.map.resources.map_aim_zoom_out
import org.jetbrains.compose.resources.stringResource

private val PinWidth = 48.dp
private val PinHeight = 60.dp

/** Distance entre le bas du dessin (ombre comprise) et la pointe de la goutte. */
private val PinTipFromBottom = 4.dp
private val PinOutline = 2.5.dp

/** Pastille et croix du « + » : lisibles à bout de bras (#649), la croix laisse 3 dp de marge dans la pastille. */
private val PinDot = 14.dp
private val PinCrossArm = 4.dp
private val PinCrossStroke = 2.dp
private val ShadowColor = Color(0xFF1E3A2A).copy(alpha = 0.18f)
private val NoticeTopMargin = 72.dp
private val ControlsEndMargin = 20.dp
private val ControlsBottomMargin = 24.dp

/** « Me localiser » de la feuille de l'etape 1 sur telephone : meme geste que l'ecran carte. */
@Composable
fun AimLocateButton(
    host: MapHostState,
    modifier: Modifier = Modifier,
) {
    val permission = rememberLocationPermissionController()
    val state by host.viewModel.state.collectAsState()
    // MapScreen n'est pas compose pendant la visee : sans ce relais, un accord obtenu ici ne
    // localiserait rien.
    LaunchedEffect(permission.status) { host.viewModel.onPermissionResult(permission.status) }
    LocateMeButton(
        isLocating = state.isLocating,
        onClick = {
            host.aim.requestLocate(state.locateSequence)
            onLocateRequested(host.viewModel, permission)
        },
        modifier = modifier,
    )
}

/**
 * Surcouche du mode visee : l'epingle fixe au centre de la zone que le chrome ne couvre pas, et en
 * grand ecran les commandes de carte que la colonne laisse sans geste de pincement. Aucun pointeur
 * n'est capte hors des boutons : la carte dessous reste entierement deplacable.
 */
@Composable
internal fun AimOverlay(
    host: MapHostState,
    modifier: Modifier = Modifier,
) {
    val aim = host.aim
    val circle = aim.circle
    val outside by remember(aim) { derivedStateOf { aim.isOutsideCircle } }
    val pinDescription =
        stringResource(if (outside) Res.string.map_aim_pin_outside_description else Res.string.map_aim_pin_description)
    val pinOffset = aim.pinOffset
    Box(modifier = modifier.fillMaxSize()) {
        if (circle != null) AimCircleLayer(host.camera, circle, pinOffset, outside)
        AimPin(
            outside = outside,
            modifier =
                Modifier
                    .align(Alignment.Center)
                    // La pointe, et non le centre du dessin, tombe sur la cible.
                    .offset(x = pinOffset.x, y = pinOffset.y - (PinHeight / 2 - PinTipFromBottom))
                    .semantics { contentDescription = pinDescription },
        )
        if (circle != null) AimDistanceChip(aim, pinOffset, outside)
        AimLocationNotice(
            host = host,
            modifier = Modifier.align(Alignment.TopCenter).padding(top = NoticeTopMargin),
        )
        if (LocalFruxLayoutMode.current == FruxLayoutMode.Expanded) {
            AimMapControls(
                host = host,
                modifier =
                    Modifier
                        .align(Alignment.BottomEnd)
                        .padding(end = ControlsEndMargin, bottom = ControlsBottomMargin),
            )
        }
    }
}

/** Couches de la visee au-dessus de la carte : l'overlay de visee, ou l'epingle figee hors visee. */
@Composable
internal fun BoxScope.AimLayers(host: MapHostState) {
    if (host.aim.isActive) AimOverlay(host)
    FrozenAimPin(host)
}

/** Epingle de la position figee (etapes 2 et 3, grand ecran) : ancree sur la carte, pas sur le centre. */
@Composable
internal fun BoxScope.FrozenAimPin(host: MapHostState) {
    val camera = host.camera
    val location = host.aim.frozenPosition
    if (host.aim.isActive || location == null || LocalFruxLayoutMode.current != FruxLayoutMode.Expanded) return
    val description = stringResource(Res.string.map_aim_pin_description)
    AimPin(
        outside = false,
        modifier =
            Modifier
                .align(Alignment.Center)
                .offset {
                    val offset = offsetOfLocation(camera.position, location)
                    IntOffset(
                        offset.x.roundToPx(),
                        (offset.y - (PinHeight / 2 - PinTipFromBottom)).roundToPx(),
                    )
                }.semantics { contentDescription = description },
    )
}

/** Retour d'un refus ou d'une position introuvable, par le bandeau de l'ecran carte. */
@Composable
private fun AimLocationNotice(
    host: MapHostState,
    modifier: Modifier = Modifier,
) {
    val viewModel = host.viewModel
    val state by viewModel.state.collectAsState()
    val permission = rememberLocationPermissionController()
    // Le bouton a son propre controleur (il vit aussi dans la feuille de l'etape 1) : un accord
    // obtenu par « Autoriser » du bandeau ne met a jour que celui-ci, il doit donc relayer aussi.
    LaunchedEffect(permission.status) { viewModel.onPermissionResult(permission.status) }
    val notice = state.locationNotice ?: return
    LocationNoticeBanner(
        notice = notice,
        shouldExplain = permission.shouldExplain,
        canOpenAppSettings = permission.canOpenAppSettings,
        onAction = {
            when (notice) {
                LocationNotice.PERMISSION_DENIED -> {
                    viewModel.onLocateRequestedPendingPermission()
                    permission.request()
                }

                LocationNotice.PERMISSION_PERMANENTLY_DENIED -> {
                    permission.openAppSettings()
                }

                LocationNotice.UNAVAILABLE -> {
                    Unit
                }
            }
        },
        onDismiss = viewModel::dismissLocationNotice,
        modifier = modifier,
    )
}

@Composable
private fun AimMapControls(
    host: MapHostState,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.End,
        verticalArrangement =
            androidx.compose.foundation.layout.Arrangement
                .spacedBy(12.dp),
    ) {
        Surface(
            shape = RoundedCornerShape(16.dp),
            color = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.onSurface,
            shadowElevation = 2.dp,
        ) {
            Column {
                FruxIconButton(
                    icon = Icons.Filled.Add,
                    contentDescription = stringResource(Res.string.map_aim_zoom_in),
                    onClick = { host.zoomBy(1f) },
                )
                FruxIconButton(
                    icon = Icons.Filled.Remove,
                    contentDescription = stringResource(Res.string.map_aim_zoom_out),
                    onClick = { host.zoomBy(-1f) },
                )
            }
        }
        AimLocateButton(host)
    }
}

private fun MapHostState.zoomBy(delta: Float) {
    val current = camera.position
    camera.moveTo(MapCameraPosition(current.target, (current.zoom + delta).coerceIn(MIN_MAP_ZOOM, MAX_MAP_ZOOM)))
}

/**
 * Goutte de l'artboard `Main`, dessinee au `Canvas` : une ombre portee ne se pose pas sur une
 * forme concave (gel Skia sur web), l'ellipse au sol la remplace.
 */
@Composable
private fun AimPin(
    outside: Boolean,
    modifier: Modifier = Modifier,
) {
    val fill = if (outside) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary
    val outline = MaterialTheme.colorScheme.surface
    val dot = MaterialTheme.colorScheme.onPrimary
    Canvas(modifier = modifier.size(PinWidth, PinHeight)) {
        drawPin(fill = fill, outline = outline, dot = dot)
    }
}

private fun DrawScope.drawPin(
    fill: Color,
    outline: Color,
    dot: Color,
) {
    val scale = size.width / PinWidth.toPx()
    val tipY = size.height - PinTipFromBottom.toPx() * scale
    val center = Offset(size.width / 2, size.width / 2)
    val radius = size.width / 2 - PinOutline.toPx()

    drawOval(
        color = ShadowColor,
        topLeft = Offset(center.x - radius * 0.55f, tipY - radius * 0.12f),
        size = Size(radius * 1.1f, radius * 0.3f),
    )

    val drop =
        Path().apply {
            moveTo(center.x, tipY)
            cubicTo(center.x, tipY, center.x - radius, center.y + radius * 0.9f, center.x - radius, center.y)
            arcTo(Rect(center.x - radius, center.y - radius, center.x + radius, center.y + radius), 180f, 180f, false)
            cubicTo(center.x + radius, center.y + radius * 0.9f, center.x, tipY, center.x, tipY)
            close()
        }
    drawPath(drop, color = fill)
    drawPath(drop, color = outline, style = Stroke(width = PinOutline.toPx()))

    drawCircle(color = dot, radius = PinDot.toPx() / 2, center = center)
    val arm = PinCrossArm.toPx()
    val cross = PinCrossStroke.toPx()
    drawLine(fill, Offset(center.x - arm, center.y), Offset(center.x + arm, center.y), cross, StrokeCap.Round)
    drawLine(fill, Offset(center.x, center.y - arm), Offset(center.x, center.y + arm), cross, StrokeCap.Round)
}
